/** @jsx jsx */
import { jsx } from '@emotion/core'
import React from 'react'
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import { orderBy } from 'lodash'
import styled from '@themes/theme'

am4core.options.commercialLicense = true

const AdvAm4ChartWrap = styled.div`
  display: block;
  width: 100%;
`

const AdvAm4Chart = styled.div`
  width: 100%;
  svg path {
    fill: inherit;
  }
`

const AdvAm4Legend = styled.div`
  width: 100%;
  height: 100%;
  svg path {
    fill: inherit;
  }
`

interface PieChartProps {
  chartId: string
  data: any[]
  order?: {
    field?: string
    desc?: string
  }
  series?: {
    name?: string
    value?: string
    color?: string
  }
  innerRadius?: number
  isLabel?: boolean
  isTicks?: boolean
  legend?: {
    width?: number
    height?: number
  }
  tooltip?: string
  stroke?: {
    color: string
    width: number
    opacity: number
  }
}

class PieChart extends React.PureComponent<PieChartProps> {
  private chart = null

  componentDidMount() {
    const { chartId } = this.props
    const chart = am4core.create(chartId, am4charts.PieChart)
    this.createdChart(chart)
  }

  componentWillUnmount() {
    if (this.chart) {
      this.chart.dispose()
    }
  }

  createdChart(chart) {
    const { chartId, data, series, innerRadius, isLabel, isTicks, tooltip, legend, order, stroke } = this.props
    const orderByOption = [[], []]
    if (order) {
      if (order.field && order.desc) {
        orderByOption[0].push(order.field)
        orderByOption[1].push(order.desc)
      } else {
        orderByOption[0].push('value')
        orderByOption[1].push('desc')
      }
    } else {
      orderByOption[0].push('value')
      orderByOption[1].push('desc')
    }
    if (data) {
      chart.data = orderBy(data, orderByOption[0], orderByOption[1])
    }
    const pieSeries = chart.series.push(new am4charts.PieSeries())
    if (series) {
      if (series.name) {
        pieSeries.dataFields.category = series.name
      } else {
        pieSeries.dataFields.category = 'name'
      }
      if (series.value) {
        pieSeries.dataFields.value = series.value
      } else {
        pieSeries.dataFields.value = 'value'
      }
      if (series.color) {
        pieSeries.slices.template.propertyFields.fill = series.color
      }
    } else {
      pieSeries.dataFields.category = 'name'
      pieSeries.dataFields.value = 'value'
    }

    if (innerRadius) {
      chart.innerRadius = am4core.percent(innerRadius)
    }

    pieSeries.labels.template.disabled = !isLabel
    pieSeries.ticks.template.disabled = !isTicks

    if (tooltip) {
      pieSeries.slices.template.tooltipText = tooltip
    } else {
      pieSeries.slices.template.tooltipText = ''
    }

    if (legend) {
      chart.legend = new am4charts.Legend()
      const legendContainer = am4core.create(`${chartId}_legend`, am4core.Container)
      legendContainer.width = am4core.percent(100)
      legendContainer.height = am4core.percent(100)
      chart.legend.parent = legendContainer
      chart.legend.valueLabels.template.text = null
      chart.legend.useDefaultMarker = true
      const markerTemplate = chart.legend.markers.template
      if (legend.height > 0 && legend.width > 0) {
        markerTemplate.width = legend.width
        markerTemplate.height = legend.height
      } else {
        markerTemplate.width = 10
        markerTemplate.height = 10
      }
    }

    if (stroke) {
      pieSeries.slices.template.stroke = am4core.color(stroke.color)
      pieSeries.slices.template.strokeWidth = stroke.width
      pieSeries.slices.template.strokeOpacity = stroke.opacity
    }

    pieSeries.slices.template.states.getKey('active').properties.shiftRadius = 0
    pieSeries.slices.template.states.getKey('hover').properties.scale = 1

    this.chart = chart
  }

  render() {
    const { chartId } = this.props
    return (
      <AdvAm4ChartWrap>
        <AdvAm4Chart id={chartId} />
        <AdvAm4Legend id={`${chartId}_legend`} />
      </AdvAm4ChartWrap>
    )
  }
}

export default PieChart
